import React, { useState } from 'react';
import {
  Box,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { isPacketFormComplete } from './parentPacketNavigation';
import { getFriendlyFormName, getPacketPanelTitle } from './parentPacketFriendlyNames';
import {
  getPacketFormIndexActionLabel,
  getPacketFormIndexStatus,
} from './parentPacketFormStatus';

function StatusLegendItem({ icon, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon}
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

export default function PacketFormIndex({
  forms,
  currentIndex,
  onSelect,
  token,
  siteSlug,
  siteName,
  /** 'default' | 'rail' — rail is a sticky sidebar (Woodmere desktop). */
  variant = 'default',
  /** Optional brand accents { primary, highlight, progressTrack }. */
  brand = null,
}) {
  const isRail = variant === 'rail';
  const [open, setOpen] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  if (!forms || !forms.length) return null;

  const completeCount = forms.filter(isPacketFormComplete).length;
  const total = forms.length;
  const progressPct = total ? Math.round((completeCount / total) * 100) : 0;
  const panelTitle = getPacketPanelTitle(siteName, siteSlug);
  const progressBarColor = brand?.primary || 'success.main';
  const progressTrack = brand?.progressTrack || '#e8eaed';
  const currentHighlight = brand?.highlight || null;

  const jumpTo = (index) => {
    onSelect(index);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: isRail ? 0 : 2,
        p: 2,
        borderRadius: 2,
        bgcolor: isRail ? '#fff' : '#f7f8fa',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {panelTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {completeCount} of {total} complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              mt: 1.25,
              height: 8,
              borderRadius: 4,
              bgcolor: progressTrack,
              '& .MuiLinearProgress-bar': { bgcolor: progressBarColor, borderRadius: 4 },
            }}
          />
        </Box>
        {!isRail && (
          <>
            <IconButton
              size="small"
              aria-label="Form list options"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ ml: 0.5, mt: -0.5 }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setOpen((o) => !o);
                }}
              >
                {open ? 'Hide form list' : 'Show form list'}
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      {(open || isRail) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mt: 1.5,
            mb: 1.5,
            py: 0.5,
          }}
        >
          <StatusLegendItem
            icon={<CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} aria-hidden="true" />}
            label="Complete"
          />
          <StatusLegendItem
            icon={<TimelapseIcon sx={{ fontSize: 16, color: 'warning.dark' }} aria-hidden="true" />}
            label="In progress"
          />
          <StatusLegendItem
            icon={<RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} aria-hidden="true" />}
            label="Not started"
          />
        </Box>
      )}

      {(open || isRail) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {forms.map((f, i) => {
            const formId = f.form_id;
            const status = getPacketFormIndexStatus(f, { token, formId });
            const friendlyName = getFriendlyFormName(siteSlug, formId, f.form_name);
            const actionLabel = getPacketFormIndexActionLabel(status);
            const isCurrent = i === currentIndex;

            let statusIcon;
            if (status === 'complete') {
              statusIcon = <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} aria-hidden="true" />;
            } else if (status === 'in_progress') {
              statusIcon = <TimelapseIcon sx={{ fontSize: 20, color: 'warning.dark' }} aria-hidden="true" />;
            } else {
              statusIcon = <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: 'text.disabled' }} aria-hidden="true" />;
            }

            return (
              <Box
                key={formId || i}
                role="button"
                tabIndex={0}
                aria-current={isCurrent ? 'step' : undefined}
                onClick={() => jumpTo(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    jumpTo(i);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: isRail ? 1 : 1.25,
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: isCurrent && brand?.primary ? brand.primary : 'divider',
                  bgcolor: isCurrent && currentHighlight
                    ? currentHighlight
                    : (status === 'in_progress' ? '#fff4e5' : '#fff'),
                  boxShadow: isCurrent && !brand
                    ? '0 0 0 1px rgba(25, 118, 210, 0.35)'
                    : 'none',
                  '&:hover': {
                    bgcolor: isCurrent && currentHighlight
                      ? currentHighlight
                      : (status === 'in_progress' ? '#ffe8cc' : 'action.hover'),
                  },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {statusIcon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Form {i + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                    {friendlyName}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color={brand?.primary ? undefined : 'primary'}
                  sx={{
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                    ...(brand?.primary ? { color: brand.primary } : {}),
                  }}
                >
                  {actionLabel} →
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
